import { z } from 'zod'
import { PlacedPartSchema } from '../domain/assembly'
import { AssemblyConnectionSchema } from '../domain/connection'
import { SimulationObstacleSchema } from '../domain/simulation-obstacle'
import { RobotGroupSchema } from '../domain/robot-group'
import {
  CodingProgramBlockSchema,
  DEFAULT_CODING_PROGRAM_NAME,
  createDefaultCodingProgram,
} from '../domain/coding'

const STORAGE_KEY = 'pingpongland:studio-session:v2'

const MotorControlSchema = z.object({
  direction: z.enum(['forward', 'stop', 'reverse']),
  speedPercent: z.number().min(0).max(100),
})

const StudioSessionSchema = z.object({
  version: z.literal(2),
  parts: z.array(PlacedPartSchema),
  connections: z.array(AssemblyConnectionSchema),
  robotGroups: z.array(RobotGroupSchema).default([]),
  motorControls: z.record(z.string(), MotorControlSchema).default({}),
  simulationObstacles: z.array(SimulationObstacleSchema).default([]),
  codingProgramName: z.string().max(30).default(DEFAULT_CODING_PROGRAM_NAME),
  codingProgram: z.array(CodingProgramBlockSchema).default(createDefaultCodingProgram()),
  settings: z.object({
    mode: z.enum(['assembly', 'coding', 'simulation']),
    environment: z.enum(['flat', 'ramp', 'road', 'obstacle']),
    autoSnap: z.boolean(),
    gravity: z.boolean(),
    preventOverlap: z.boolean(),
    solidFloor: z.boolean(),
  }),
  selection: z.object({
    selectedPartId: z.string(),
    selectedInstanceId: z.string().nullable(),
    selectedInstanceIds: z.array(z.string()),
  }),
})

export type StudioSession = z.infer<typeof StudioSessionSchema>

function getStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export const studioSessionRepository = {
  save(session: StudioSession) {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(session))
  },

  load(): StudioSession | null {
    const raw = getStorage()?.getItem(STORAGE_KEY)
    if (!raw) return null

    try {
      return StudioSessionSchema.parse(JSON.parse(raw))
    } catch {
      return null
    }
  },

  clear() {
    getStorage()?.removeItem(STORAGE_KEY)
  },
}
